import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  ArrowLeft, 
  LogOut, 
  Users, 
  AlertTriangle,
  RefreshCw,
  CheckCircle,
  Shield,
  Eye
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminForceLogout({ user }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState("");
  const [selectedSubGroup, setSelectedSubGroup] = useState("");
  const [showPdpaAgain, setShowPdpaAgain] = useState(true);
  const [reason, setReason] = useState("");
  
  const [preview, setPreview] = useState(null);
  const [confirmExecute, setConfirmExecute] = useState(false);

  useEffect(() => {
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    try {
      const response = await axios.get(`${API}/admin/force-logout/groups`, { withCredentials: true });
      setGroups(response.data.groups);
    } catch (error) {
      console.error("Error fetching groups:", error);
      toast.error("Failed to load groups");
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = async () => {
    if (!selectedGroup || !selectedSubGroup) {
      toast.error("Please select a group and sub-group");
      return;
    }

    setPreviewing(true);
    setPreview(null);
    try {
      const response = await axios.post(`${API}/admin/force-logout/preview`, {
        group: selectedGroup,
        sub_group: selectedSubGroup
      }, { withCredentials: true });
      setPreview(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to preview");
    } finally {
      setPreviewing(false);
    }
  };

  const handleExecute = async () => {
    if (!confirmExecute) {
      toast.error("Please confirm you want to proceed");
      return;
    }

    setExecuting(true);
    try {
      const response = await axios.post(`${API}/admin/force-logout/execute`, {
        group: selectedGroup,
        sub_group: selectedSubGroup,
        show_pdpa_again: showPdpaAgain,
        reason: reason || "Admin initiated force logout"
      }, { withCredentials: true });
      
      toast.success(response.data.message);
      
      // Reset form
      setPreview(null);
      setConfirmExecute(false);
      setReason("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to execute force logout");
    } finally {
      setExecuting(false);
    }
  };

  const currentGroup = groups.find(g => g.id === selectedGroup);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-yellow-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/admin")}
            className="text-slate-400 hover:text-white"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <LogOut className="w-6 h-6 text-red-400" />
              Force Logout Users
            </h1>
            <p className="text-slate-400 text-sm">Log out users by group and optionally require PDPA re-acknowledgment</p>
          </div>
        </div>

        {/* Warning Banner */}
        <Card className="p-4 bg-red-500/10 border-red-500/50 mb-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-red-400">Use with caution</h3>
              <p className="text-sm text-slate-300">
                This will immediately log out all selected users. They will need to log in again.
                If "Show PDPA again" is enabled, they will also need to re-acknowledge the consent terms.
              </p>
            </div>
          </div>
        </Card>

        {/* Selection Card */}
        <Card className="p-6 bg-slate-800/50 border-slate-700 mb-6">
          <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-400" />
            Select User Group
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {/* Main Group */}
            <div>
              <label className="text-sm text-slate-400 mb-2 block">Main Group</label>
              <Select value={selectedGroup} onValueChange={(v) => {
                setSelectedGroup(v);
                setSelectedSubGroup("");
                setPreview(null);
              }}>
                <SelectTrigger className="bg-slate-900 border-slate-600 text-white">
                  <SelectValue placeholder="Select group..." />
                </SelectTrigger>
                <SelectContent>
                  {groups.map(g => (
                    <SelectItem key={g.id} value={g.id}>
                      <div className="flex flex-col">
                        <span>{g.name}</span>
                        <span className="text-xs text-slate-400">{g.description}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Sub Group */}
            <div>
              <label className="text-sm text-slate-400 mb-2 block">Sub-Group / Filter</label>
              <Select 
                value={selectedSubGroup} 
                onValueChange={(v) => {
                  setSelectedSubGroup(v);
                  setPreview(null);
                }}
                disabled={!selectedGroup}
              >
                <SelectTrigger className="bg-slate-900 border-slate-600 text-white">
                  <SelectValue placeholder="Select sub-group..." />
                </SelectTrigger>
                <SelectContent>
                  {currentGroup?.sub_groups?.map(sg => (
                    <SelectItem key={sg.id} value={sg.id}>
                      {sg.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Options */}
          <div className="space-y-4 border-t border-slate-700 pt-4">
            <label className="flex items-center gap-3 cursor-pointer">
              <Checkbox
                checked={showPdpaAgain}
                onCheckedChange={setShowPdpaAgain}
              />
              <div>
                <span className="text-white">Show PDPA/Consent form again on next login</span>
                <p className="text-xs text-slate-400">Users will need to re-acknowledge all consent clauses</p>
              </div>
            </label>

            <div>
              <label className="text-sm text-slate-400 mb-2 block">Reason (optional - for audit log)</label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g., Updated terms and conditions, Security requirement..."
                className="bg-slate-900 border-slate-600 text-white"
                rows={2}
              />
            </div>
          </div>

          {/* Preview Button */}
          <div className="mt-6">
            <Button
              onClick={handlePreview}
              disabled={!selectedGroup || !selectedSubGroup || previewing}
              className="w-full bg-blue-600 hover:bg-blue-700"
            >
              {previewing ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Loading preview...
                </>
              ) : (
                <>
                  <Eye className="w-4 h-4 mr-2" />
                  Preview Affected Users
                </>
              )}
            </Button>
          </div>
        </Card>

        {/* Preview Results */}
        {preview && (
          <Card className="p-6 bg-slate-800/50 border-slate-700 mb-6">
            <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-yellow-400" />
              Preview: {preview.count} Users Will Be Logged Out
            </h3>

            {preview.count === 0 ? (
              <p className="text-slate-400">No users match this criteria.</p>
            ) : (
              <>
                <div className="max-h-64 overflow-y-auto mb-4">
                  <table className="w-full text-sm">
                    <thead className="text-slate-400 border-b border-slate-700">
                      <tr>
                        <th className="text-left py-2">Name</th>
                        <th className="text-left py-2">Email</th>
                        <th className="text-left py-2">Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.users.map((u, i) => (
                        <tr key={i} className="border-b border-slate-700/50">
                          <td className="py-2 text-white">{u.name}</td>
                          <td className="py-2 text-slate-300">{u.email}</td>
                          <td className="py-2">
                            <span className="text-xs bg-slate-700 px-2 py-1 rounded">
                              {u.member_type || 'N/A'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {preview.truncated && (
                  <p className="text-sm text-yellow-400 mb-4">
                    Showing first 50 of {preview.count} users
                  </p>
                )}

                {/* Confirmation */}
                <div className="border-t border-slate-700 pt-4 space-y-4">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <Checkbox
                      checked={confirmExecute}
                      onCheckedChange={setConfirmExecute}
                    />
                    <span className="text-red-400 font-semibold">
                      I confirm I want to force logout {preview.count} users
                    </span>
                  </label>

                  <Button
                    onClick={handleExecute}
                    disabled={!confirmExecute || executing}
                    className="w-full bg-red-600 hover:bg-red-700"
                  >
                    {executing ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Executing...
                      </>
                    ) : (
                      <>
                        <LogOut className="w-4 h-4 mr-2" />
                        Execute Force Logout ({preview.count} users)
                      </>
                    )}
                  </Button>
                </div>
              </>
            )}
          </Card>
        )}

        {/* Info Card */}
        <Card className="p-4 bg-blue-500/10 border-blue-500/30">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-slate-300">
              <p className="font-semibold text-blue-400 mb-1">What happens when users log in again?</p>
              <ul className="list-disc list-inside space-y-1">
                <li>If "Show PDPA again" is enabled, they will see the consent form</li>
                <li>They must agree to all required clauses before accessing the portal</li>
                <li>Their acknowledgment is recorded in the audit log</li>
              </ul>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
